//#region Imports
import { MotionPanel } from "@/components/common/MotionPanel";
import { AppUrlPath } from "@/lib/AppUrlPath";
import {
  MutationSystem,
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { useUserStore } from "@/stores/userStore";
import type { AgendaItemMotion, Meeting, MotionType, User } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  EllipsisIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDisableTeam } from "@/components/page/disableTeam";
import { useMotionTypeMap } from "@/hooks/useMotionTypeMap";
import { cn } from "@repo/shared/utils";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import { pullUrlParams } from "@/lib/urlParams";
import type { MeetingRunContext } from "./MeetingRunContext";
import { SectionsNavBar } from "./SectionsNavBar";
import { type OpenPanels, useOpenPanels } from "./itemSections/useOpenPanels";
import { type SectionId, sections } from "./itemSections/sections";
import { CollapsibleSideBar } from "./CollapsibleSideBar";
import { useGetAgendaItem } from "@/hooks/queryHooks/agendaItemHooks";
import { useGetMeeting } from "@/hooks/queryHooks/meetingHooks";
import { useGetTeam } from "@/hooks/queryHooks/teamHooks";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { meetingRepo, type MeetingDto } from "@/repositories/meetingRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";
//#endregion

const buttonClasses =
  /*@tw*/ "cursor-pointer mb-1 min-h-6 px-2 py-1 rounded-md hover:bg-accent/40";

const buttonHighlightedClasses = /*@tw*/ "bg-accent/40";

// #region Add Meet Mot

const MeetingRunAddMeetingMotion = ({
  tenantId,
  meeting: _meeting,
  mutationSystem,
  motionTypeMap,
  navigateScreen,
}: {
  tenantId: string;
  meeting: Meeting;
  mutationSystem: MutationSystem;
  motionTypeMap: {
    item: Record<string, MotionType>;
    meeting: Record<string, MotionType>;
  };
  navigateScreen: (
    screen: string | undefined,
    direction: "" | "prev" | "next"
  ) => void;
}) => {
  const mutationKey = "meeting:" + _meeting.id;

  const meetingMutation: Mutator<MeetingDto> = useFutureCallback(
    async (id, data) => {
      await meetingRepo.update(tenantId, id, data);
    }
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _meeting
  );

  const insertMotion = (motion: MotionType) => {
    // prepare new motion
    const newMotion: AgendaItemMotion = {
      motionId: motion.id,
      timestamp: Date.now(),
      resolved: !motion.voting,
    };

    // prepare review motions array
    const motions = meeting.review?.motions ?? [];

    // find deepest unresolved nesting
    let list: AgendaItemMotion[] = motions;
    let lastMotion = list[list.length - 1];
    while (
      lastMotion &&
      !lastMotion.resolved &&
      motionTypeMap.meeting[lastMotion.motionId]?.nestable
    ) {
      list = lastMotion.nesting ??= [];
      lastMotion = list[list.length - 1];
    }

    // add this motion to the unresolved nesting
    list.push(newMotion);

    // update meeting
    mutationSystem.debounce(
      mutationKey,
      meetingMutation,
      { review: { ...meeting.review, motions } },
      meeting.id
    );
  };

  return (
    <MotionPanel
      motionFor="meeting"
      className="absolute bottom-4"
      label={
        <>
          Meeting
          <br />
          Motion
        </>
      }
      onPickMotion={(motion) => {
        navigateScreen("motions", "");
        insertMotion(motion);
      }}
    />
  );
};

// #endregion

// #region Sidebar

const MeetingRunSidebar = ({
  tenantId,
  noTimestamps,
  meeting,
  meetingItem,
  mutationSystem,
  motionTypeMap,
  navigateScreen,
}: {
  tenantId: string;
  noTimestamps: boolean;
  meeting?: Meeting;
  meetingItem: string;
  mutationSystem: MutationSystem;
  motionTypeMap?: {
    item: Record<string, MotionType>;
    meeting: Record<string, MotionType>;
  };
  navigateScreen: (
    screen: string | undefined,
    direction: "" | "prev" | "next"
  ) => void;
}) => {
  const [, rawRefresh] = useState({});
  const refresh = () => rawRefresh({});
  const id = useId();

  const iconClasses =
    /*@tw*/ "inline-block -mt-[3px] text-white p-0.5 rounded-full";

  return (
    <CollapsibleSideBar collapsedWidth={90}>
      <div className="w-80 flex-shrink-0 bg-accent/20 flex flex-col pb-[60px] overflow-y-auto">
        <div className="p-4">
          <div
            onClick={() => navigateScreen("roll-call", "")}
            className={cn(
              buttonClasses,
              meetingItem === "roll-call" && buttonHighlightedClasses
            )}
          >
            Roll Call
          </div>
          {meeting?.agenda?.agendaModules?.map((agendaModule) => (
            <div key={agendaModule.id}>
              <p className="font-bold mt-2 mb-1">{agendaModule.name}</p>
              {agendaModule.agendaItems?.map((agendaItem) => {
                // use latest agenda item from mutationSystem, and NOT the item from the meeting
                // nested data, which updates slowly and inconsistently.
                const dataKey = "agendaItem:" + agendaItem.id;
                agendaItem = mutationSystem.initObject(dataKey, agendaItem);
                mutationSystem.addRefresher(dataKey, id, refresh);

                const approval = agendaItem.approver?.approverType;
                const started =
                  agendaItem.review?.moved && agendaItem.review?.seconded;
                const status = agendaItem.review?.status;

                return (
                  <div
                    key={agendaItem.id}
                    className={cn(
                      buttonClasses,
                      meetingItem === agendaItem.id && buttonHighlightedClasses
                    )}
                    onClick={() => navigateScreen(agendaItem.id, "")}
                  >
                    {approval !== "team" ? (
                      agendaItem.review?.timestampEnd ? (
                        <ChevronRightIcon
                          size={20}
                          className={cn(iconClasses, "bg-tertiary")}
                        />
                      ) : (
                        <ChevronRightIcon
                          size={20}
                          className={cn(iconClasses, "text-accent")}
                        />
                      )
                    ) : started && status === "approved" ? (
                      <CheckIcon
                        size={20}
                        className={cn(iconClasses, "bg-tertiary")}
                      />
                    ) : started && status === "rejected" ? (
                      <XIcon
                        size={20}
                        className={cn(iconClasses, "bg-destructive")}
                      />
                    ) : started && status === "deferred" ? (
                      <ArrowRightIcon
                        size={20}
                        className={cn(iconClasses, "bg-secondary")}
                      />
                    ) : (
                      <EllipsisIcon
                        size={20}
                        className={cn(iconClasses, "bg-accent/50")}
                      />
                    )}{" "}
                    {agendaItem.name}
                  </div>
                );
              })}
            </div>
          )) ?? <div>No Agenda</div>}
          <hr className="my-2" />
          <div
            onClick={() => navigateScreen("motions", "")}
            className={cn(
              buttonClasses,
              meetingItem === "motions" && buttonHighlightedClasses
            )}
          >
            Meeting Motions
          </div>
        </div>
        <div className="flex-grow" />
        <div className="flex flex-row p-4">
          {tenantId && meeting?.id && motionTypeMap ? (
            !noTimestamps && (
              <MeetingRunAddMeetingMotion
                tenantId={tenantId}
                meeting={meeting}
                mutationSystem={mutationSystem}
                motionTypeMap={motionTypeMap}
                navigateScreen={navigateScreen}
              />
            )
          ) : (
            <Spinner />
          )}
        </div>
      </div>
    </CollapsibleSideBar>
  );
};

// #endregion

// #region Page

// root page component
export const MeetingRunPage = () => {
  useDisableTeam();
  useRoleProtected("view");

  const { noTimestamps: noTimestampsKey } = pullUrlParams(true);
  const [noTimestamps] = useState(noTimestampsKey === "true");

  const params = useParams();
  const tenantId = params.tenantId ?? "";
  const meetingId = params.meetingId ?? "";
  const meetingItem = params.meetingItem ?? "";
  const itemSection = params.itemSection ?? "";

  const mutationSystem = useMemo(() => new MutationSystem(), []);

  const navigate = useNavigate();
  const currentPath = useLocation();
  const { data: meeting } = useGetMeeting(tenantId, meetingId);
  const { data: team } = useGetTeam(tenantId, meeting?.teamId ?? "");
  const { data: users } = useUserList(tenantId);
  const user = useUserStore((s) => s.user);

  const collapsedByDefaultSections = useMemo<SectionId[]>(
    () => ["present"],
    []
  );
  const openSections: OpenPanels = useOpenPanels(
    useMemo(() => sections.map((section) => section.id), []),
    collapsedByDefaultSections
  );

  const navigateToSection = (sectionId: string) => {
    navigate({
      pathname: currentPath.pathname,
      search: currentPath.search,
      hash: `#${sectionId}-meeting-section`,
    });
    openSections.setValueById(sectionId, true);
  };

  const activeTeamId = useActiveTeamIdOrOrg();
  const teamMismatch =
    !!activeTeamId && !!meeting && meeting.teamId != activeTeamId;
  const minutesGenerated = !!meeting?.minutesData;

  const shouldLeave = teamMismatch || minutesGenerated;

  useEffect(() => {
    if (shouldLeave) navigate(new AppUrlPath(tenantId).tenantHomePath);
  }, [shouldLeave, navigate, tenantId]);

  const meetingMutation: Mutator<MeetingDto> = useFutureCallback(
    async (id, data) => {
      await meetingRepo.update(tenantId, id, data);
    }
  );

  const directionRef = useRef<"" | "prev" | "next">("");

  const userMap = useMemo(
    () =>
      users?.reduce(
        (map, user) => ((map[user.id] = user), map),
        Object.create(null) as Record<string, User>
      ),
    [users]
  );

  const agendaItem =
    useGetAgendaItem(
      tenantId,
      !meetingItem || meetingItem === "roll-call" ? "" : meetingItem
    ).data ?? null;

  useEffect(() => {
    if (!shouldLeave && !meetingItem) navigate("./roll-call");
  }, [meetingItem, shouldLeave, navigate]);

  const motionTypeMap = useMotionTypeMap();

  const navigableItems = useMemo(
    () => [
      "roll-call",
      ...([] as string[]).concat(
        ...(meeting?.agenda?.agendaModules?.map(
          (agendaModule) => agendaModule.agendaItemIds
        ) ?? [])
      ),
      "motions",
    ],
    [meeting]
  );

  const versionsCaptured = useRef(false);

  useEffect(() => {
    if (!meeting || !user || shouldLeave) return;
    if (versionsCaptured.current) return;

    versionsCaptured.current = true;

    // record the versions of items that were used for this meeting
    const itemVersions: Record<string, number> = {};

    for (const agendaModule of meeting.agenda?.agendaModules ?? []) {
      for (const agendaItem of agendaModule.agendaItems ?? []) {
        itemVersions[agendaItem.id] = agendaItem._version;
      }
    }

    // update the meeting to include this mapping
    const mutationKey = "meeting:" + meeting.id;
    mutationSystem.debounce(
      mutationKey,
      meetingMutation,
      { review: { ...meeting.review, secretary: user.id, itemVersions } },
      meeting.id
    );
  }, [meeting, shouldLeave, mutationSystem, meetingMutation, user]);

  const nonItemPages = ["roll-call", "motions"];

  const currentIndex = navigableItems.indexOf(meetingItem);
  const prevItem = navigableItems[currentIndex - 1] as string | undefined;
  const nextItem = navigableItems[currentIndex + 1] as string | undefined;

  const navigateScreen = (
    screen: string | undefined,
    direction: "" | "prev" | "next"
  ) => {
    if (screen) {
      const newPath = new AppUrlPath(tenantId).getMeetingRunPath(
        meetingId,
        screen
      );

      if (currentPath.pathname !== newPath) {
        directionRef.current = direction;
        navigate(newPath);
      }
    }
  };

  return (
    <div className="flex flex-col flex-grow">
      <div className="flex flex-row flex-grow h-0 relative">
        {/* sidebar navigation */}
        <MeetingRunSidebar
          tenantId={tenantId}
          noTimestamps={noTimestamps}
          meeting={meeting ?? undefined}
          meetingItem={meetingItem}
          mutationSystem={mutationSystem}
          motionTypeMap={motionTypeMap}
          navigateScreen={navigateScreen}
        />

        <div className="w-0 flex-grow flex flex-col">
          {/* header navigation */}

          <div className="flex flex-row items-center justify-between gap-x-4 p-2 pt-0">
            <Button
              disabled={!prevItem}
              variant="ghost"
              className="py-1 h-auto"
              onClick={() => navigateScreen(prevItem, "prev")}
            >
              <ChevronsLeftIcon />
              Previous Item
            </Button>
            {/* nav bar */}
            <SectionsNavBar
              agendaItem={agendaItem}
              mutationSystem={mutationSystem}
              navigateToSection={navigateToSection}
            />
            <Button
              disabled={!nextItem}
              variant="ghost"
              className="py-1 h-auto"
              onClick={() => navigateScreen(nextItem, "next")}
            >
              Next Item <ChevronsRightIcon />
            </Button>
          </div>

          {/* interface screen */}

          {tenantId &&
          meetingId &&
          meeting &&
          team &&
          user &&
          users &&
          userMap &&
          motionTypeMap &&
          (nonItemPages.includes(meetingItem) || agendaItem) ? (
            <Outlet
              context={
                {
                  tenantId,
                  meeting,
                  team,
                  user,
                  users,
                  userMap,
                  meetingItem,
                  agendaItem,
                  itemSection,
                  mutationSystem,
                  directionRef,
                  motionTypeMap,
                  prev: () => navigateScreen(prevItem, "prev"),
                  next: () => navigateScreen(nextItem, "next"),
                  noTimestamps,
                  openSections,
                } satisfies MeetingRunContext
              }
            />
          ) : (
            <div className="flex-grow flex flex-row items-center justify-center">
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// #endregion
